export interface TimeSlot {
  start: string;
  end: string;
  available: boolean;
  conflictingEvent?: string;
  conflictingEventIds?: string[];
}

export interface CalendarConflict {
  id: string;
  title: string;
  start: string;
  end: string;
  blockingEnd?: string;
  location?: string;
  showAs?: string;
  isAllDay?: boolean;
}

export interface AvailabilityResponse {
  success: boolean;
  staffEmail: string;
  staffName: string;
  date: string;
  bookingType: string;
  bookingDuration: number;
  slots: TimeSlot[];
  calendarConflicts?: CalendarConflict[];
  msGraphAvailable: boolean;
  error?: string;
}

interface BookingAvailabilityViewModel {
  conflictRows: CalendarConflict[];
  availableSlots: TimeSlot[];
  unavailableSlotCount: number;
}

const ukTimeFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

export function formatUKTime(value: string | Date): string {
  return ukTimeFormatter.format(new Date(value));
}

function buildLegacyConflictRows(slots: TimeSlot[]): CalendarConflict[] {
  const conflictMap = new Map<string, CalendarConflict>();

  slots.forEach((slot) => {
    if (!slot.conflictingEvent) return;

    const id = slot.conflictingEvent;
    const existing = conflictMap.get(id);
    if (!existing) {
      conflictMap.set(id, {
        id,
        title: slot.conflictingEvent,
        start: slot.start,
        end: slot.end,
      });
      return;
    }

    if (new Date(slot.start) < new Date(existing.start)) {
      existing.start = slot.start;
    }
    if (new Date(slot.end) > new Date(existing.end)) {
      existing.end = slot.end;
    }
  });

  return Array.from(conflictMap.values()).sort(
    (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
  );
}

export function buildBookingAvailabilityViewModel(
  availabilityData: AvailabilityResponse | undefined,
  now: Date = new Date()
): BookingAvailabilityViewModel {
  const slots = availabilityData?.slots || [];
  const conflictRows =
    availabilityData?.calendarConflicts && availabilityData.calendarConflicts.length > 0
      ? availabilityData.calendarConflicts
      : buildLegacyConflictRows(slots);

  const availableSlots = slots.filter((slot) => {
    return slot.available && new Date(slot.start) >= now;
  });

  return {
    conflictRows,
    availableSlots,
    unavailableSlotCount: slots.length - availableSlots.length,
  };
}
